import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowRight, CalendarDays, CheckCircle2, Mail, Sparkles } from "lucide-react";
import { api, formatApiError, useAuth } from "@/lib/api";
import { toast } from "sonner";

export default function InscriptionSuccess() {
    const [params] = useSearchParams();
    const checkoutSessionId = params.get("cs");
    const [status, setStatus] = useState("checking"); // checking | paid | pending | failed
    const [session, setSession] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!checkoutSessionId) {
            setStatus("failed");
            return;
        }

        let attempts = 0;
        const MAX = 10;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${checkoutSessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    // load session details
                    if (data.session_id) {
                        try {
                            const sr = await api.get(`/sessions/${data.session_id}`);
                            setSession(sr.data);
                        } catch {}
                    }
                    // fire confetti 🎉
                    const fire = (x) => confetti({
                        particleCount: 100, spread: 72, origin: { x, y: 0.35 },
                        colors: ["#E8873C", "#0A0A0A", "#FFFFFF", "#F7F3EE"],
                    });
                    fire(0.25); setTimeout(() => fire(0.5), 250); setTimeout(() => fire(0.75), 500);
                    return;
                }
                if (data.status === "expired") {
                    setStatus("failed");
                    toast.error("Le paiement a expiré. Réessayez.");
                    return;
                }
                attempts += 1;
                if (attempts >= MAX) {
                    setStatus("pending");
                    return;
                }
                setTimeout(poll, 2000);
            } catch (err) {
                attempts += 1;
                if (attempts >= MAX) {
                    setStatus("failed");
                    toast.error(formatApiError(err));
                    return;
                }
                setTimeout(poll, 2000);
            }
        };
        poll();
    }, [checkoutSessionId]);

    return (
        <div className="min-h-[80vh] bg-[#FAFAFA] py-20" data-testid="page-inscription-success">
            <div className="mx-auto max-w-3xl px-6">
                {status === "checking" && (
                    <div className="rounded-3xl border border-brand-line bg-white p-12 text-center shadow-premium">
                        <div className="animate-pulse">
                            <Sparkles className="mx-auto h-8 w-8 text-brand-orange" />
                            <h1 className="mt-6 font-display text-3xl">Confirmation en cours…</h1>
                            <p className="mt-3 text-brand-smoke">Nous vérifions votre paiement auprès de Stripe.</p>
                        </div>
                    </div>
                )}

                {status === "paid" && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden rounded-3xl border border-brand-line bg-white shadow-premium-lg"
                        data-testid="inscription-success-confirmed"
                    >
                        <div className="bg-brand-ink p-10 text-center text-white grain">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-orange">
                                <CheckCircle2 className="h-8 w-8 text-white" />
                            </div>
                            <div className="mt-6 text-xs uppercase tracking-[0.3em] text-brand-orange">Inscription confirmée</div>
                            <h1 className="mt-4 font-display text-4xl leading-[1.05] tracking-tightest sm:text-5xl">
                                Bienvenue, <em className="italic text-brand-orange">{user?.first_name || "cher·ère stagiaire"}</em>.
                            </h1>
                            <p className="mx-auto mt-6 max-w-xl text-base text-white/75">
                                Votre place est réservée. Un email de confirmation vient de vous être envoyé avec votre convention de formation en pièce jointe.
                            </p>
                        </div>

                        {session && (
                            <div className="p-8 md:p-10">
                                <div className="rounded-2xl border border-brand-line bg-[#FAFAFA] p-6">
                                    <div className="text-xs uppercase tracking-widest text-brand-orange">Votre session</div>
                                    <div className="mt-3 font-display text-2xl leading-tight">{session.formation_name}</div>
                                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brand-ink/80">
                                        <span className="inline-flex items-center gap-2">
                                            <CalendarDays className="h-4 w-4 text-brand-orange" />
                                            {new Date(session.start_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                                        </span>
                                        <span>{session.start_time}–{session.end_time}</span>
                                        <span>{session.venue_name} · {session.city}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col gap-3 bg-white px-8 pb-10 sm:flex-row sm:justify-center">
                            <Link
                                to="/stagiaire"
                                data-testid="success-go-dashboard"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                            >
                                Accéder à mon espace stagiaire
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/expertise-pa"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line px-8 py-3.5 text-sm font-semibold text-brand-ink hover:border-brand-orange"
                            >
                                Découvrir les autres cursus
                            </Link>
                        </div>
                        <div className="flex items-center justify-center gap-2 border-t border-brand-line bg-[#FAFAFA] p-4 text-xs text-brand-smoke">
                            <Mail className="h-3.5 w-3.5" /> Vous recevrez vos rappels J-7 et J-1 par email.
                        </div>
                    </motion.div>
                )}

                {status === "pending" && (
                    <div className="rounded-3xl border border-brand-line bg-white p-10 text-center shadow-premium">
                        <h1 className="font-display text-3xl">Paiement en cours de traitement</h1>
                        <p className="mt-4 text-brand-smoke">
                            Le paiement prend un peu plus de temps que prévu. Vous recevrez un email dès que votre inscription sera confirmée.
                        </p>
                        <Link to="/stagiaire" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-[#d9772c]">
                            Aller dans mon espace
                        </Link>
                    </div>
                )}

                {status === "failed" && (
                    <div className="rounded-3xl border border-brand-line bg-white p-10 text-center shadow-premium">
                        <h1 className="font-display text-3xl text-brand-ink">Impossible de confirmer le paiement.</h1>
                        <p className="mt-4 text-brand-smoke">
                            Si vous avez été débité·e, notre équipe vous contactera sous 24h.{" "}
                            <Link to="/contact" className="font-semibold text-brand-orange hover:underline">Contactez-nous</Link> si besoin.
                        </p>
                        <button onClick={() => navigate("/sessions")} className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-line px-6 py-3 text-sm font-semibold hover:border-brand-orange">
                            Retour aux sessions
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
